import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {RecurrenceFrequenciesEnum} from '../../../../models/api/recurrenceModel';

/*
 * Props.
 */

export interface RecurrenceFrequencyDropdownBoxProps {
  freq: RecurrenceFrequenciesEnum;
  interval: number;
  onChange: (arg: RecurrenceFrequenciesEnum) => void;
}

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

/*
 * Component.
 */

export const RecurrenceFrequencyDropdownBox: FC<RecurrenceFrequencyDropdownBoxProps> = (props) => (
  <StyledDropdownBoxCoordinator
    value={recurrenceFrequencyTextRenderers[props.freq](props.interval)}
    renderDropdown={(popoverProps) => renderRecurrenceDropdown(popoverProps, props)}
  />
);

const frequencies = [
  RecurrenceFrequenciesEnum.DAILY,
  RecurrenceFrequenciesEnum.WEEKLY,
  RecurrenceFrequenciesEnum.MONTHLY,
  RecurrenceFrequenciesEnum.YEARLY,
];

function renderRecurrenceDropdown(
  popoverProps: PopoverTransferProps,
  props: RecurrenceFrequencyDropdownBoxProps,
) {
  const {freq, interval} = props;
  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        {frequencies.map((freqOption) => {
          const isChecked = freqOption === freq;
          return (
            <DropdownItem key={freqOption} isChecked={isChecked} onSelect={() => props.onChange(freqOption)}>
              <DropdownItemContent isChecked={isChecked}>
                {recurrenceFrequencyTextRenderers[freqOption](interval)}
              </DropdownItemContent>
            </DropdownItem>
          );
        })}
      </DropdownList>
    </Dropdown>
  );
}

const recurrenceFrequencyTextRenderers: {[_: string]: RendererOf<number>} = {
  [RecurrenceFrequenciesEnum.DAILY]: (interval) => (
    <Plural id="recurrence-frequency-daily" value={interval} one="day" other="days" />
  ),
  [RecurrenceFrequenciesEnum.WEEKLY]: (interval) => (
    <Plural id="recurrence-frequency-weekly" value={interval} one="week" other="weeks" />
  ),
  [RecurrenceFrequenciesEnum.MONTHLY]: (interval) => (
    <Plural id="recurrence-frequency-monthly" value={interval} one="month" other="months" />
  ),
  [RecurrenceFrequenciesEnum.YEARLY]: (interval) => (
    <Plural id="recurrence-frequency-yearly" value={interval} one="year" other="years" />
  ),
};
